import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Expense, ExpenseSummary, MetaData } from '@/types'
import { formatRecordedAtForLedger, getCategoryBadge } from './expense-constants'
import { useExpenseCategoryDefs } from './use-expense-category-defs'
import { buildCategoryChips, filterTotals } from './expenseDashboardLogic'
import { CategoryLabel, useMoney } from './moneyUi'
import { useCogsBasis } from './use-cogs-basis'
import { cn } from '@/lib/utils'
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Plus,
  Lock,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Paperclip,
} from 'lucide-react'

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank: 'Bank',
  card: 'Card',
  other: 'Other',
}

export type ExpenseLedgerSortKey =
  | 'expense_date'
  | 'amount'
  | 'category'
  | 'description'
  | 'created_by'
  | 'type'
  | 'created_at'

type Props = {
  expenses?: Expense[]
  meta?: MetaData
  /** Totals for the period + search + method (NOT the category), so the
   *  chips stay put while one of them is selected (F-RPT-N37). */
  summary?: ExpenseSummary
  periodLabel: string
  page: number
  setPage: (p: number) => void
  category: string
  setCategory: (c: string) => void
  searchInput: string
  onSearchInputChange: (s: string) => void
  method: string
  setMethod: (m: string) => void
  sortBy: ExpenseLedgerSortKey
  setSortBy: (k: ExpenseLedgerSortKey) => void
  sortDir: 'asc' | 'desc'
  setSortDir: (d: 'asc' | 'desc') => void
  onAdd: () => void
  onEdit: (e: Expense) => void
  onDelete: (id: string) => void
  /** F-CTRL-N79: admin only — correct a Day Close paid-out the server marks `editable_linked`. */
  onCorrectPaidOut?: (e: Expense) => void
  onManageCategories?: () => void
  /** Accountant view: no add / edit / delete controls. Export stays. */
  readOnly?: boolean
  /** F-RPT-N43: the Scan receipt button, when this tenant scans receipts. */
  scanAction?: React.ReactNode
  /** F-RPT-N43: open the receipt a row was approved from. */
  onOpenReceipt?: (scanId: string) => void
}

/**
 * The ledger (F-FE-N129): the place you find one expense and total many.
 *
 * Category chips with totals answer "how much maintenance this period" in one
 * click and filter the table at the same time; the totals strip sums exactly
 * the rows the filter matches (opex and stock apart); search reaches the
 * description, payee, reference, notes and vendor name. The period comes from
 * the page's bar, so it is the same window the Overview and the P&L show.
 */
export function ExpenseLedgerTab({
  expenses,
  meta,
  summary,
  periodLabel,
  page,
  setPage,
  category,
  setCategory,
  searchInput,
  onSearchInputChange,
  method,
  setMethod,
  sortBy,
  setSortBy,
  sortDir,
  setSortDir,
  onAdd,
  onEdit,
  onDelete,
  onCorrectPaidOut,
  onManageCategories,
  readOnly = false,
  scanAction,
  onOpenReceipt,
}: Props) {
  const money = useMoney()
  const { data: categoryDefs = [] } = useExpenseCategoryDefs()
  const { data: cogsBasis } = useCogsBasis()

  const chips = buildCategoryChips(summary, categoryDefs)
  const totals = filterTotals(summary, category)
  const activeChip = chips.find(c => c.slug === category)
  const hasFilter = !!(category || method || searchInput.trim())


  const clearFilters = () => {
    setCategory('')
    setMethod('')
    onSearchInputChange('')
    setPage(1)
  }

  // Toggle sort like the Staff Management table: clicking the active column flips
  // direction, clicking a new column switches to it and starts ascending.
  const handleSort = (key: ExpenseLedgerSortKey) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  const renderSortIcon = (key: ExpenseLedgerSortKey) => {
    if (sortBy !== key) return <ArrowUpDown className="ml-2 h-3.5 w-3.5 opacity-50" />
    return sortDir === 'asc' ? <ArrowUp className="ml-2 h-3.5 w-3.5" /> : <ArrowDown className="ml-2 h-3.5 w-3.5" />
  }

  const SortableHeader = ({
    label,
    sortKey,
    align = 'left',
  }: {
    label: string
    sortKey: ExpenseLedgerSortKey
    align?: 'left' | 'right'
  }) => {
    const isActive = sortBy === sortKey
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={() => handleSort(sortKey)}
        className={cn(
          'h-8 -mx-2 whitespace-nowrap px-2 font-medium hover:bg-transparent',
          isActive ? 'text-foreground hover:text-foreground' : 'text-muted-foreground hover:text-foreground',
          align === 'right' && 'ml-auto',
        )}
      >
        {label}
        {renderSortIcon(sortKey)}
      </Button>
    )
  }

  const Chip = ({
    active,
    onClick,
    label,
    amount,
    count,
    stock,
    swatch,
  }: {
    active: boolean
    onClick: () => void
    label: string
    amount: number
    count: number
    stock?: boolean
    swatch?: string
  }) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-pressed={active}
          className={cn(
            'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            active ? 'border-foreground bg-foreground text-background' : 'bg-background text-foreground hover:bg-muted',
          )}
        >
          {/* `bg-current` paints the dot in the badge pair's text colour — the
              saturated half. The pair's own background is a near-white tint. */}
          {swatch && <span className={cn('h-2 w-2 rounded-full', swatch, 'bg-current dark:bg-current')} aria-hidden />}
          <span className="font-medium">{label}</span>
          <span className={cn('tabular-nums', active ? 'text-background/85' : 'text-muted-foreground')}>{money.whole(amount)}</span>
          {stock && <span className={cn('text-xs', active ? 'text-background/70' : 'text-muted-foreground')}>stock</span>}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {count} {count === 1 ? 'entry' : 'entries'} in this period.
        {stock ? ' Stock reaches the profit and loss as cost of goods when it is sold, not as an operating expense.' : ''}
        {active && label !== 'All' ? ' Select again to show every category.' : ''}
      </TooltipContent>
    </Tooltip>
  )

  return (
    <div className="space-y-4">
      {/* Category chips: one click answers "how much did I spend on X" AND
          filters the table to those rows. Totals follow the search / method
          filters, so they always describe what a click would show. */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Chip
            active={!category}
            onClick={() => { setCategory(''); setPage(1) }}
            label="All"
            amount={summary?.grand_total ?? 0}
            count={summary?.count ?? 0}
          />
          {chips.map(c => (
            <Chip
              key={c.slug}
              active={category === c.slug}
              onClick={() => { setCategory(category === c.slug ? '' : c.slug); setPage(1) }}
              label={c.label}
              amount={c.total}
              count={c.count}
              stock={c.isStock}
              swatch={c.color}
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search description, paid to, reference, notes…"
            value={searchInput}
            onChange={e => onSearchInputChange(e.target.value)}
          />
        </div>
        <Select
          value={method || '__all__'}
          onValueChange={v => {
            setMethod(v === '__all__' ? '' : v)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Paid by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any method</SelectItem>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="bank">Bank</SelectItem>
            <SelectItem value="card">Card</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        {hasFilter && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            Clear
          </Button>
        )}
        <div className="ml-auto flex shrink-0 flex-wrap gap-2">
          {/* F-FE-N141: Export moved to the page header, beside the period bar,
              the way Reports does it — it is a page action, not a filter. */}
          {!readOnly && onManageCategories && (
            <Button type="button" variant="outline" size="sm" onClick={onManageCategories}>
              Categories
            </Button>
          )}
          {!readOnly && scanAction}
          {!readOnly && (
            <Button type="button" onClick={onAdd}>
              <Plus className="mr-1 h-4 w-4" />
              Add expense
            </Button>
          )}
        </div>
      </div>

      {/* Totals for exactly the rows the filter matches (F-RPT-N37). Stock is
          shown beside opex, never inside it — it reaches the P&L as COGS. */}
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 rounded-lg border bg-muted/30 px-4 py-2.5 text-sm">
        <span className="text-muted-foreground">
          {totals.count} {totals.count === 1 ? 'entry' : 'entries'} · {periodLabel}
          {activeChip ? ` · ${activeChip.label}` : ''}
          {method ? ` · ${PAYMENT_METHOD_LABELS[method] ?? method}` : ''}
          {searchInput.trim() ? ` · “${searchInput.trim()}”` : ''}
        </span>
        <span>
          Operating expenses <strong className="font-semibold tabular-nums">{money.full(totals.opex)}</strong>
        </span>
        {totals.stock > 0.005 && (
          <span>
            Stock purchases <strong className="font-semibold tabular-nums">{money.full(totals.stock)}</strong>
            <span className="ml-1 text-xs text-muted-foreground">
              {cogsBasis?.basis === 'purchases' ? 'counted as cost of goods when bought' : 'counted as cost of goods when sold'}
            </span>
          </span>
        )}
      </div>

      <Card className="overflow-hidden p-0">
        <div className="max-h-[min(70vh,640px)] overflow-auto">
          <table className="w-full min-w-[600px] md:min-w-[720px] lg:min-w-[860px] text-sm">
            <thead className="sticky top-0 z-30 border-b bg-muted">
              <tr>
                <th className="p-3 text-left font-medium">
                  <SortableHeader label="Date & time" sortKey="expense_date" />
                </th>
                <th className="p-3 text-left font-medium">
                  <SortableHeader label="Category" sortKey="category" />
                </th>
                <th className="p-3 text-left font-medium">
                  <SortableHeader label="Description" sortKey="description" />
                </th>
                <th className="p-3 text-right font-medium">
                  <div className="flex justify-end">
                    <SortableHeader label="Amount" sortKey="amount" align="right" />
                  </div>
                </th>
                <th className="hidden xl:table-cell whitespace-nowrap p-3 text-left font-medium text-muted-foreground">Paid to</th>
                <th className="hidden xl:table-cell whitespace-nowrap p-3 text-left font-medium text-muted-foreground">Method and reference</th>
                <th className="hidden lg:table-cell p-3 text-left font-medium">
                  <SortableHeader label="Created by" sortKey="created_by" />
                </th>
                <th className="hidden md:table-cell p-3 text-left font-medium">
                  <SortableHeader label="Type" sortKey="type" />
                </th>
                {!readOnly && (
                  <th className="sticky right-0 z-20 min-w-[148px] border-l bg-muted p-3 text-right font-medium text-muted-foreground">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {expenses && expenses.length > 0 ? (
                expenses.map(e => {
                  const badge = getCategoryBadge(e.category, categoryDefs)
                  const isAutoLinked = !!e.reference_type
                  return (
                    <tr key={e.id} className="hover:bg-muted/40">
                      <td className="whitespace-nowrap p-3 tabular-nums text-muted-foreground">
                        {formatRecordedAtForLedger(e.recorded_at, e.expense_date)}
                      </td>
                      <td className="p-3">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="-mx-1.5 rounded-md px-1.5 py-0.5 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              onClick={() => { setCategory(e.category); setPage(1) }}
                            >
                              <CategoryLabel label={badge.label} color={badge.color} className="whitespace-nowrap" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right">Show only {badge.label}</TooltipContent>
                        </Tooltip>
                      </td>
                      <td className="max-w-xs truncate p-3" title={e.notes ? `${e.description ?? ''}\n${e.notes}` : undefined}>
                        {e.receipt_scan_id && onOpenReceipt ? (
                          <button
                            type="button"
                            className="mr-1.5 inline-flex align-text-bottom text-muted-foreground hover:text-foreground"
                            aria-label="Open the receipt"
                            title="Open the receipt"
                            onClick={() => onOpenReceipt(e.receipt_scan_id!)}
                          >
                            <Paperclip className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                        {e.description || '—'}
                        {e.notes ? <span className="ml-1 text-xs text-muted-foreground">· {e.notes}</span> : null}
                      </td>
                      {/* F-FE-N164: an expense is not an error, so the column is
                          ink. A credit says so in words. */}
                      <td className="whitespace-nowrap p-3 text-right font-medium tabular-nums text-foreground">
                        {e.amount < 0 && <span className="mr-2 text-xs font-normal text-success-fg">credit</span>}
                        {money.full(e.amount)}
                      </td>
                      <td className="hidden xl:table-cell max-w-[160px] truncate p-3 text-muted-foreground">
                        {e.vendor_name || e.payee || '—'}
                      </td>
                      <td className="hidden xl:table-cell p-3 text-muted-foreground">
                        <span className="whitespace-nowrap">
                          {e.payment_method ? PAYMENT_METHOD_LABELS[e.payment_method] ?? e.payment_method : '—'}
                          {e.reference_no ? <span className="ml-1.5 text-xs tabular-nums">{e.reference_no}</span> : null}
                        </span>
                      </td>
                      <td className="hidden lg:table-cell whitespace-nowrap p-3 text-muted-foreground">{e.created_by_name || '—'}</td>
                      <td className="hidden md:table-cell p-3">
                        {isAutoLinked ? (
                          <Badge variant="secondary" className="gap-1">
                            <Lock className="h-3 w-3" />
                            Auto
                          </Badge>
                        ) : (
                          <Badge variant="outline">Manual</Badge>
                        )}
                      </td>
                      {!readOnly && (
                        <td className="sticky right-0 z-10 border-l bg-card p-3 text-right">
                          {!isAutoLinked ? (
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => onEdit(e)}>
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:bg-danger-surface hover:text-danger-fg"
                                onClick={() => onDelete(e.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          ) : e.editable_linked && onCorrectPaidOut ? (
                            <div className="flex justify-end">
                              <Button variant="ghost" size="sm" onClick={() => onCorrectPaidOut(e)}>
                                Edit
                              </Button>
                            </div>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span tabIndex={0} className="inline-flex h-9 cursor-help items-center rounded-sm text-xs text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                                  Read-only
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="left">
                                Created by a stock movement. Change it where it was recorded, in Store Inventory.
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={readOnly ? 8 : 9} className="p-10 text-center text-muted-foreground">
                    {hasFilter ? (
                      <div className="space-y-2">
                        <p>No expenses match this filter for {periodLabel}.</p>
                        <Button variant="outline" size="sm" onClick={clearFilters}>
                          Clear filters
                        </Button>
                      </div>
                    ) : (
                      <p>No expenses recorded for {periodLabel}.</p>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {meta && meta.total > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {meta.total_pages > 1 ? `Page ${meta.current_page} of ${meta.total_pages} · ` : ''}
            {meta.total} {meta.total === 1 ? 'entry' : 'entries'}
          </span>
          {meta.total_pages > 1 && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= meta.total_pages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
